import { StyleSheet } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

// Extended chat colors (adding to MARKETPLACE_COLORS as needed)
export const CHAT_COLORS = {
  ...MARKETPLACE_COLORS,
  messageBubbleReceived: '#2F2F2F',
  textLabel: 'rgba(238, 241, 244, 0.8)',
  textTimestamp: 'rgba(238, 241, 244, 0.7)',
  borderLight: '#3F3F3F',
  accentBlueDark: '#4672FE',
  buttonInactive: '#5E5E5E',
  avatarBackground: 'rgba(238, 241, 244, 0.1)',
} as const;

// Shared chat styles used across SellerChatModal and InboxConversationScreen
export const chatStyles = StyleSheet.create({
  // Date Separator
  dateSeparator: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  dateSeparatorText: {
    fontSize: 12,
    fontWeight: '300',
    color: CHAT_COLORS.textSecondary,
    lineHeight: 18,
  },

  // Message Groups
  messageGroup: {
    marginBottom: 16,
  },
  messagesInGroup: {
    gap: 16,
  },
  messagesContainer: {
    paddingHorizontal: 24,
  },

  // Sent Message (User - Right aligned, Blue)
  sentMessageContainer: {
    alignItems: 'flex-end',
  },
  sentMessageBubble: {
    backgroundColor: CHAT_COLORS.accentBlueDark,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    maxWidth: '80%',
    minWidth: 100,
    gap: 1,
  },
  messageContent: {
    gap: 2,
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  sentMessageLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: CHAT_COLORS.textLabel,
    lineHeight: 18,
    flexShrink: 0,
  },
  sentMessageText: {
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Received Message (Other user - Left aligned, Gray)
  receivedMessageContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  userAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: CHAT_COLORS.avatarBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userAvatarText: {
    fontSize: 12,
    fontWeight: '600',
    color: CHAT_COLORS.textPrimary,
    textTransform: 'uppercase',
  },
  receivedMessageBubble: {
    backgroundColor: CHAT_COLORS.messageBubbleReceived,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    maxWidth: '80%',
    minWidth: 100,
    gap: 1,
  },
  receivedMessageLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: CHAT_COLORS.textSecondary,
    lineHeight: 18,
    flexShrink: 0,
  },
  receivedMessageText: {
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Timestamp
  timestampContainer: {
    alignItems: 'flex-end',
  },
  timestampText: {
    fontSize: 12,
    fontWeight: '300',
    color: CHAT_COLORS.textTimestamp,
    lineHeight: 18,
  },

  // Menu Button (for 3-dot menu on messages)
  menuButton: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Input Bar
  inputBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 24,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: CHAT_COLORS.dividerGray,
  },
  attachButton: {
    paddingTop: 5,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_COLORS.textPrimary,
    lineHeight: 21,
    paddingVertical: 6,
    maxHeight: 100,
  },
  sendButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonActive: {
    backgroundColor: CHAT_COLORS.accentBlueDark,
  },
  sendButtonInactive: {
    backgroundColor: CHAT_COLORS.buttonInactive,
  },

  // Chat Header
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 18,
    borderBottomWidth: 1,
    borderBottomColor: CHAT_COLORS.dividerGray,
    gap: 20,
  },
  chatHeaderContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  chatThumbnail: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 0.45,
    borderColor: CHAT_COLORS.borderLight,
  },
  chatThumbnailPlaceholder: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatHeaderTextContainer: {
    flex: 1,
    gap: 2,
  },
  chatTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: CHAT_COLORS.textPrimary,
    lineHeight: 21,
  },
  chatSubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: CHAT_COLORS.textSecondary,
    lineHeight: 18,
  },

  // Chat Content
  chatContent: {
    flex: 1,
  },
  chatContentContainer: {
    flexGrow: 1,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 44,
  },
  emptyIconContainer: {
    marginBottom: 18,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: CHAT_COLORS.textPrimary,
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 2,
  },
  emptySubtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_COLORS.textMuted,
    lineHeight: 21,
    textAlign: 'center',
    width: 240,
  },

  // Quick Replies
  quickRepliesContainer: {
    alignItems: 'flex-end',
    paddingHorizontal: 24,
    gap: 14,
    marginBottom: 24,
  },
  quickReplyButton: {
    borderWidth: 1,
    borderColor: CHAT_COLORS.accentBlueDark,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  quickReplyText: {
    fontSize: 14,
    fontWeight: '500',
    color: CHAT_COLORS.accentBlue,
    lineHeight: 21,
    textAlign: 'center',
  },
});

